import { useState, useEffect, useCallback } from 'react';
import type { ExecutionState } from '../../hooks/useSessionEvents';
import type { QueuedMessage } from '../../hooks/useMessageQueue';

interface ExecutionControlBarProps {
  executionState: ExecutionState;
  queueCount: number;
  queue: QueuedMessage[];
  onStop: () => void;
  onRemoveQueueItem: (id: string) => void;
  onClearQueue: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRedirect?: () => void;
  inputHasText?: boolean;
}

export function ExecutionControlBar({
  executionState,
  queueCount,
  queue,
  onStop,
  onRemoveQueueItem,
  onClearQueue,
  onReorder,
  onRedirect,
  inputHasText,
}: ExecutionControlBarProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Auto-collapse when queue empties
  useEffect(() => {
    if (queueCount === 0) {
      setExpanded(false);
      setSelectedIndex(null);
    }
  }, [queueCount]);

  // Clear selection when it goes out of bounds
  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= queueCount) {
      setSelectedIndex(queueCount > 0 ? queueCount - 1 : null);
    }
  }, [queueCount, selectedIndex]);

  // Keyboard shortcuts for queue management
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!expanded || queueCount === 0) return;

    const ctrlOrMeta = e.ctrlKey || e.metaKey;

    if (e.key === 'Escape') {
      e.preventDefault();
      setSelectedIndex(null);
      return;
    }

    if (e.key === 'ArrowDown' && !ctrlOrMeta) {
      e.preventDefault();
      setSelectedIndex(prev => {
        if (prev === null) return 0;
        return Math.min(prev + 1, queueCount - 1);
      });
      return;
    }

    if (e.key === 'ArrowUp' && !ctrlOrMeta) {
      e.preventDefault();
      setSelectedIndex(prev => {
        if (prev === null) return queueCount - 1;
        return Math.max(prev - 1, 0);
      });
      return;
    }

    if (selectedIndex === null) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      onRemoveQueueItem(queue[selectedIndex].id);
      return;
    }

    if (e.key === 'ArrowDown' && ctrlOrMeta && selectedIndex < queueCount - 1) {
      e.preventDefault();
      onReorder(selectedIndex, selectedIndex + 1);
      setSelectedIndex(selectedIndex + 1);
      return;
    }

    if (e.key === 'ArrowUp' && ctrlOrMeta && selectedIndex > 0) {
      e.preventDefault();
      onReorder(selectedIndex, selectedIndex - 1);
      setSelectedIndex(selectedIndex - 1);
    }
  }, [expanded, queueCount, selectedIndex, queue, onRemoveQueueItem, onReorder]);

  useEffect(() => {
    if (!expanded) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expanded, handleKeyDown]);

  if (executionState === 'idle') return null;

  return (
    <div data-testid="execution-control-bar" className="border-t border-border bg-muted/30 px-4 py-2 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {executionState === 'running' && (
            <span data-testid="execution-status-running" className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-muted-foreground">Agent is working...</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {executionState === 'running' && inputHasText && onRedirect && (
            <button
              data-testid="redirect-button"
              onClick={onRedirect}
              className="rounded-md px-2.5 py-1 text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
            >
              Redirect
            </button>
          )}
          <button
            data-testid="stop-button"
            onClick={onStop}
            className="rounded-md px-2.5 py-1 text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
          >
            Stop
          </button>
        </div>
      </div>

      {queueCount > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <button
              data-testid="queue-expand-toggle"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
              <span data-testid="queue-count">{queueCount} message{queueCount !== 1 ? 's' : ''} queued</span>
            </button>
            <button
              data-testid="clear-queue-button"
              onClick={onClearQueue}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          </div>
          {expanded && (
            <div data-testid="queue-list" className="space-y-0.5">
              {queue.map((m, index) => (
                <div
                  key={m.id}
                  data-testid={`queue-item-${m.id}`}
                  aria-selected={selectedIndex === index}
                  onClick={() => setSelectedIndex(index)}
                  className={`flex items-center gap-1.5 text-xs text-muted-foreground bg-background rounded px-2 py-1 cursor-pointer transition-colors ${selectedIndex === index ? 'ring-1 ring-primary bg-primary/5' : 'hover:bg-accent/50'}`}
                >
                  <span className="truncate flex-1">{m.content}</span>
                  <button
                    data-testid={`queue-item-up-${m.id}`}
                    onClick={(e) => { e.stopPropagation(); if (index > 0) onReorder(index, index - 1); }}
                    disabled={index === 0}
                    className="text-muted-foreground/50 hover:text-foreground shrink-0 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                  </button>
                  <button
                    data-testid={`queue-item-down-${m.id}`}
                    onClick={(e) => { e.stopPropagation(); if (index < queue.length - 1) onReorder(index, index + 1); }}
                    disabled={index === queue.length - 1}
                    className="text-muted-foreground/50 hover:text-foreground shrink-0 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                  </button>
                  <button
                    data-testid={`queue-item-remove-${m.id}`}
                    onClick={(e) => { e.stopPropagation(); onRemoveQueueItem(m.id); }}
                    className="text-muted-foreground/50 hover:text-foreground shrink-0"
                    aria-label="Remove"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
